<template>
  <!-- 主页深色科技风数据大屏：左 320px / 中 flex:1 / 右 260px 三栏 -->
  <div class="home-dashboard">
    <!-- ==================== 左栏 ==================== -->
    <div class="col col-left">
      <!-- 到期提醒：直接复用现有轮播组件，仅做深色皮肤覆盖 -->
      <div class="panel">
        <div class="panel-hd">
          <span class="panel-title">到期提醒</span>
          <span class="panel-sub">EXPIRY ALERT</span>
        </div>
        <div class="panel-bd carousel-bd">
          <expiry-carousel />
        </div>
      </div>

      <!-- 设备统计：总台数 / 监护仪 / 呼吸机 三个大数字 -->
      <div class="panel">
        <div class="panel-hd">
          <span class="panel-title">设备统计</span>
          <span class="panel-sub">EQUIPMENT OVERVIEW</span>
        </div>
        <div class="panel-bd">
          <div class="stat-item" v-for="s in stats" :key="s.label" :title="s.hint || ''">
            <div class="stat-label">{{ s.label }}</div>
            <div class="stat-value">
              {{ s.value === null ? '--' : s.value }}<span class="stat-unit">{{ s.unit }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 盘点结论：取最近一个年度盘点批次（panel-grow 撑满左栏剩余空间） -->
      <div class="panel panel-grow">
        <div class="panel-hd">
          <span class="panel-title">盘点结论</span>
          <span class="panel-sub">INVENTORY RESULT</span>
        </div>
        <div class="panel-bd">
          <template v-if="latestBatch">
            <div class="inv-name">{{ latestBatch.name || `${latestBatch.year} 年度盘点` }}</div>
            <div class="inv-row">
              <span class="inv-key">盘点年度</span>
              <span class="inv-val">{{ latestBatch.year || '-' }}</span>
            </div>
            <div class="inv-row">
              <span class="inv-key">创建日期</span>
              <span class="inv-val">{{ formatDate(latestBatch.created_at) }}</span>
            </div>
            <div class="inv-row">
              <span class="inv-key">批次状态</span>
              <span class="inv-val" :class="latestBatch.status === 'closed' ? 'inv-done' : 'inv-ongoing'">
                {{ batchStatusText(latestBatch.status) }}
              </span>
            </div>
          </template>
          <div v-else class="mini-empty">暂无盘点数据</div>
        </div>
      </div>

    </div>

    <!-- ==================== 中栏：接单池 ==================== -->
    <div class="col col-mid">
      <div class="panel panel-fill">
        <div class="panel-hd">
          <span class="panel-title">
            接 单 池
            <span class="pool-count">共 {{ orders.length }} 单</span>
          </span>
          <span class="panel-hd-right">
            <span class="pool-refresh">{{ lastRefreshText }}</span>
            <el-button type="text" size="mini" class="pool-refresh-btn" @click="refreshPool">刷新</el-button>
            <span class="panel-sub">REPAIR POOL</span>
          </span>
        </div>
        <div class="panel-bd">
          <div
            v-for="o in orders"
            :key="o.order_no"
            class="order-card"
            :class="{ 'is-emergency': o.is_emergency, 'is-urgent': !o.is_emergency && o.is_urgent }"
          >
            <!-- 左侧：工单号 + 设备名 + 科室 + 故障描述（单行截断） -->
            <div class="oc-left">
              <div class="oc-line1">
                <span class="oc-no">{{ o.order_no }}</span>
                <span class="oc-name">{{ o.equipment_name || '-' }}</span>
                <el-tag v-if="o.is_emergency" type="danger" size="mini">急救</el-tag>
                <el-tag v-else-if="o.is_urgent" type="warning" size="mini">加急</el-tag>
              </div>
              <div class="oc-line2">
                <span class="oc-dept">{{ o.department || '-' }}</span>
                <span class="oc-fault">{{ o.fault_desc || '无故障描述' }}</span>
              </div>
            </div>
            <!-- 右侧：等待分钟数 + 去处理 -->
            <div class="oc-right">
              <span class="oc-wait" :class="{ 'is-overdue': o.overdue }">
                <span v-if="o.overdue" class="oc-clock">⏰</span>
                {{ o.waiting_minutes === null || o.waiting_minutes === undefined ? '--' : o.waiting_minutes }} 分钟
              </span>
              <el-button type="text" size="mini" class="oc-btn" @click="goRepair">去处理</el-button>
            </div>
          </div>

          <!-- 空态：不用 ✅ emoji，部分浏览器会把它渲染成一块大白框，深色大屏上很突兀 -->
          <div v-if="!loading && !orders.length" class="empty-tip">当前没有待接单工单</div>
          <div v-if="loading && !orders.length" class="empty-tip">加载中…</div>
        </div>
      </div>
    </div>

    <!-- ==================== 右栏：监护明细 ==================== -->
    <div class="col col-right">
      <div class="panel panel-fill">
        <!-- 整条标题栏可点击：跳到监护统计页（深色大屏风格，可录入 + 查历史）。
             把点击挂在外层而不是仅标题上，是为了扩大点选范围，站在屏前也好点。 -->
        <div class="panel-hd panel-hd-link" title="点击进入监护统计" @click="goMonitorStat">
          <span class="panel-title panel-link">
            监护明细<i class="el-icon-arrow-right link-arrow"></i>
          </span>
          <!-- 右侧只放统计日期（用户拍板：去掉英文小标，仅留日期） -->
          <span v-if="dashboard.person_date" class="panel-sub">{{ dashboard.person_date }}</span>
        </div>
        <div class="panel-bd">
          <template v-if="dashboard.rows.length">
            <!-- 列头：与数据行同宽对齐 -->
            <div class="moni-row moni-head">
              <span class="moni-dept">科室名称</span>
              <span class="moni-num">监护人次</span>
              <span class="moni-num">监护台数</span>
            </div>
            <div class="moni-row" v-for="r in dashboard.rows" :key="r.department">
              <span class="moni-dept">{{ r.department }}</span>
              <span class="moni-num">{{ r.monitor_person_times || 0 }}<i>人次</i></span>
              <span class="moni-num">{{ r.monitor_devices == null ? '-' : r.monitor_devices }}<i>台</i></span>
            </div>
            <div class="moni-row moni-sum">
              <span class="moni-dept">合计</span>
              <span class="moni-num">{{ dashboard.person_total }}<i>人次</i></span>
              <span class="moni-num" :title="deviceTotalHint">{{ dashboard.device_total }}<i>台</i></span>
            </div>
          </template>
          <div v-else class="mini-empty">暂无数据，请先上传当日统计图片</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ExpiryCarousel from './ExpiryCarousel.vue'
import equipmentApi from '../api/equipment'
import repairApi from '../api/repair'
import inventoryApi from '../api/inventory'
import monitorStatApi from '../api/monitorStat'

// 接单池轮询间隔（毫秒）：30 秒（2026-11-02 按现场要求从 3 分钟改回）。
// 3 分钟对新工单来说太滞后，护士报修后大屏上要等好几分钟才冒出来。
// 安全性：tick() 里 document.hidden 时直接返回（页面不可见不请求），
// refreshPool() 内部有 refreshing 防重入，切回前台还会立即补刷一次，
// 所以 30 秒不会堆积请求；接口是轻量列表查询，一天 2880 次对 2C4G 无压力。
const POOL_POLL_INTERVAL = 30 * 1000
// 盘点批次状态中文映射（与后端 inventory/models.py STATUS_CHOICES 保持一致）
const BATCH_STATUS_TEXT = {
  ongoing: '进行中',
  closed: '已核对',
}

export default {
  name: 'HomeDashboard',
  components: { ExpiryCarousel },
  data() {
    return {
      // 三个统计卡：总台数（实时台账）/ 监护仪台数（一期取 Excel 清单合计）/ 监护人次（当天图片合计）
      // unit 字段取代模板里写死的「台」，监护人次单位为人次
      stats: [
        { label: '设备总台数', value: null, unit: '台', source: 'equipment', params: {} },
        // 监护仪台数：**一期由后端写死 130 台**（monitor_stat/config.py 的
        // DEVICE_TOTAL_OVERRIDE，因为设备台账还没录全、Excel 清单也不完整）。
        // 后期台账录全后改后端一处即可，前端这里不用动。
        {
          label: '监护仪台数',
          value: null,
          unit: '台',
          source: 'monitor',
          hint: '一期按全院固定 130 台显示（设备台账未录全），后期按设备台账统计',
        },
        { label: '监护人次', value: null, unit: '人次', source: 'monitor' },
      ],
      // 监护统计大屏数据（/api/monitor-stat/dashboard/）
      dashboard: {
        person_date: null,
        person_total: 0,
        device_total: 0,
        // 台数是否来自一期固定值（后端标记），用于给数字加悬浮说明
        device_total_fixed: false,
        rows: [],
      },
      // 接单池工单列表
      orders: [],
      loading: false,
      // 最近一个盘点批次（后端按 -year 排序，取第 1 条）
      latestBatch: null,
      // 轮询定时器句柄，beforeDestroy 里清除
      timer: null,
      // 上一次刷新成功的时间（大屏上显示，用来判断「到底还在不在刷」）
      lastRefreshAt: null,
      // 防重入：慢网络下上一轮没回来就跳过本轮，避免请求堆积
      refreshing: false,
    }
  },
  computed: {
    lastRefreshText() {
      if (!this.lastRefreshAt) return '每 3 分钟自动刷新'
      const p = n => (n < 10 ? '0' + n : '' + n)
      const d = this.lastRefreshAt
      return `已刷新 ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    },
    // 台数合计是后端写死的占位值时才提示，取真实数据时不打扰
    deviceTotalHint() {
      return this.dashboard.device_total_fixed
        ? '一期按全院固定 130 台显示（设备台账未录全），后期按设备台账统计'
        : ''
    },
  },
  mounted() {
    this.fetchStats()
    this.fetchLatestBatch()
    // 首次加载与定时轮询走同一条路径，保证「刷新时间」和防重入逻辑一致
    this.refreshPool()
    this.timer = setInterval(this.tick, POOL_POLL_INTERVAL)
    // 标签页切回前台时立即补刷一次，否则挂墙屏切回来会是几分钟前的旧数据
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },
  methods: {
    // 定时回调：页面不可见（切到后台/最小化）时跳过，不白跑请求
    tick() {
      if (typeof document !== 'undefined' && document.hidden) return
      this.refreshPool()
    },
    onVisibilityChange() {
      if (!document.hidden) this.refreshPool()
    },
    // 刷新接单池 + 监护明细（标题栏「刷新」按钮和定时器共用，内部防重入）
    async refreshPool() {
      if (this.refreshing) return
      this.refreshing = true
      try {
        const results = await Promise.all([this.fetchOrders(), this.fetchDashboard()])
        // 只有全部成功才更新时间：失败时把「已刷新」停在最后一次成功的时间上，
        // 挂墙屏上一眼就能看出是不是断了，而不是假装刚刷过。
        if (results.every(Boolean)) this.lastRefreshAt = new Date()
      } finally {
        this.refreshing = false
      }
    },
    // 设备台数：仅「设备总台数」走实时台账；监护仪台数/监护人次由大屏接口填充
    async fetchStats() {
      // 找出 source==='equipment' 的卡片（当前只有「设备总台数」）
      const settled = await Promise.all(
        this.stats.map(s =>
          s.source === 'equipment'
            ? equipmentApi
                .getEquipmentCount(s.params || {})
                .then(res => (res.data && typeof res.data.count === 'number' ? res.data.count : null))
                .catch(() => null)
            : Promise.resolve(null)
        )
      )
      const next = this.stats.map((s, i) => Object.assign({}, s, {
        // 监护仪/监护人次的 value 由 fetchDashboard 写入，这里只动 equipment 项
        value: s.source === 'equipment' ? settled[i] : s.value,
      }))
      this.stats = next
    },
    // 监护统计大屏：监护人次（当天图片合计）/ 监护仪台数（Excel 清单合计）/ 分科室明细
    async fetchDashboard() {
      try {
        const res = await monitorStatApi.getDashboard()
        const d = res.data || {}
        this.dashboard = {
          person_date: d.person_date || null,
          person_total: d.person_total || 0,
          device_total: d.device_total || 0,
          device_total_fixed: !!d.device_total_fixed,
          rows: Array.isArray(d.rows) ? d.rows : [],
        }
        // 回填两个监护类统计卡（监护人次 / 监护仪台数）
        this.stats = this.stats.map(s => {
          if (s.source !== 'monitor') return s
          if (s.label === '监护仪台数') return Object.assign({}, s, { value: this.dashboard.device_total })
          if (s.label === '监护人次') return Object.assign({}, s, { value: this.dashboard.person_total })
          return s
        })
        return true
      } catch (e) {
        // 后端未启动 / 无权限时静默失败，保持上一次数据，不打断大屏展示
        return false
      }
    },
    // 接单池：PENDING 工单，后端已按 急救 > 加急 > 先来先接 排好序
    async fetchOrders() {
      this.loading = true
      try {
        const res = await repairApi.getPool({ page: 1, page_size: 50 })
        const list = res.data.results || res.data
        this.orders = Array.isArray(list) ? list : []
        return true
      } catch (e) {
        // 失败时**保留上一次的工单**，不能清空：
        // 大屏上一次网络抖动就把列表清掉，看起来像「当前没有待接单工单」，会误导人。
        return false
      } finally {
        this.loading = false
      }
    },
    // 盘点批次：接口直接返回数组（非分页），按 year 倒序，取第一条
    async fetchLatestBatch() {
      try {
        const res = await inventoryApi.listBatches()
        const list = res.data && res.data.results ? res.data.results : res.data
        this.latestBatch = Array.isArray(list) && list.length ? list[0] : null
      } catch (e) {
        this.latestBatch = null
      }
    },
    // 批次状态转中文
    batchStatusText(status) {
      return BATCH_STATUS_TEXT[status] || status || '-'
    },
    // created_at 是 ISO 时间串，只取日期部分
    formatDate(v) {
      if (!v) return '-'
      return String(v).slice(0, 10)
    },
    // 「去处理」跳到故障报修页
    goRepair() {
      this.$router.push('/equipment/repair')
    },
    // 「监护明细」标题点击 → 监护统计页（录入 + 历史查询）
    goMonitorStat() {
      this.$router.push('/monitor-stat')
    },
  },
}
</script>

<style scoped>
/* ===== 大屏容器：铺满 el-main 可视区，不做整页滚动，滚动交给各面板内部 ===== */
.home-dashboard {
  position: absolute;
  inset: 0;
  display: flex;
  gap: 12px;
  padding: 12px;
  box-sizing: border-box;
  background: #0a1628;
  /* 底纹：极淡的青色径向渐变，模仿监控大屏的背光 */
  background-image: radial-gradient(ellipse at 50% 0%, rgba(34, 211, 238, 0.06) 0%, rgba(10, 22, 40, 0) 60%);
  overflow: hidden;
}

/* ===== 三栏 ===== */
.col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  overflow: auto;
}
.col-left {
  flex: 0 0 320px;
  width: 320px;
}
.col-mid {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}
.col-right {
  flex: 0 0 260px;
  width: 260px;
  overflow: hidden;
}

/* ===== 面板通用外观：深蓝底 + 青灰描边 + 微弱外发光 ===== */
.panel {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #0f2137;
  border: 1px solid #1b3a5c;
  border-radius: 6px;
  box-shadow: 0 0 12px rgba(34, 211, 238, 0.08);
  overflow: hidden;
}
/* 占满所在栏的剩余高度 */
.panel-fill {
  flex: 1 1 auto;
}
.panel-grow {
  flex: 1 1 auto;
}
/* 标题行：左中文 / 右英文小标，下方一条青色渐变分隔线 */
.panel-hd {
  position: relative;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 9px;
}
.panel-hd::after {
  content: '';
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 0;
  height: 1px;
  background: linear-gradient(90deg, rgba(34, 211, 238, 0) 0%, rgba(34, 211, 238, 0.55) 50%, rgba(34, 211, 238, 0) 100%);
}
.panel-title {
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1px;
}
/* 可点击的标题栏：整条都能点，hover 时底色微亮 + 标题变青色 + 箭头右移 */
.panel-hd-link {
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.panel-hd-link:hover {
  background-color: rgba(34, 211, 238, 0.06);
}
.panel-link {
  cursor: pointer;
  transition: color 0.15s ease;
}
.panel-hd-link:hover .panel-link {
  color: #22d3ee;
}
.link-arrow {
  margin-left: 4px;
  font-size: 12px;
  font-weight: normal;
  color: #22d3ee;
  opacity: 0.45;
  transition: all 0.15s ease;
}
.panel-link:hover .link-arrow {
  opacity: 1;
  transform: translateX(2px);
}
.panel-sub {
  color: #6b8ca8;
  font-size: 10px;
  letter-spacing: 1px;
}
.panel-bd {
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px;
  overflow: auto;
}
.flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}
.placeholder-text {
  color: #5b7691;
  font-size: 14px;
  letter-spacing: 2px;
}
.mini-empty {
  color: #5b7691;
  font-size: 13px;
  padding: 6px 0;
}

/* ===== 设备统计：大号青色发光数字 ===== */
.stat-item {
  background: rgba(34, 211, 238, 0.04);
  border: 1px solid rgba(27, 58, 92, 0.9);
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;
}
.stat-item:last-child {
  margin-bottom: 0;
}
.stat-label {
  color: #8fa8bf;
  font-size: 12px;
  letter-spacing: 1px;
}
.stat-value {
  color: #22d3ee;
  font-size: 32px;
  font-weight: bold;
  line-height: 1.2;
  text-shadow: 0 0 10px rgba(34, 211, 238, 0.55);
}
.stat-unit {
  color: #6b8ca8;
  font-size: 12px;
  font-weight: normal;
  margin-left: 4px;
  text-shadow: none;
}

/* ===== 盘点结论 ===== */
.inv-name {
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.inv-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  line-height: 22px;
}
.inv-key {
  color: #6b8ca8;
}
.inv-val {
  color: #c8dbe8;
}
.inv-ongoing {
  color: #e6a23c;
}
.inv-done {
  color: #22d3ee;
}

/* ===== 接单池 ===== */
.pool-count {
  color: #6b8ca8;
  font-size: 11px;
  font-weight: normal;
  margin-left: 8px;
}
/* 标题栏右侧：刷新时间 + 手动刷新 + 英文小标 */
.panel-hd-right {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.pool-refresh {
  color: #6b8ca8;
  font-size: 10px;
  letter-spacing: 0.5px;
}
/* 深色大屏上 el-button--text 默认蓝色偏暗，改成青色才看得清 */
.pool-refresh-btn {
  color: #22d3ee !important;
  font-size: 11px;
  padding: 0;
}
.pool-refresh-btn:hover {
  color: #67e8f9 !important;
  text-decoration: underline;
}
.order-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  margin-bottom: 8px;
  border-radius: 6px;
  background: #12263c;
  border: 1px solid #1b3a5c;
  border-left: 3px solid #1b3a5c;
}
/* 急救：红色左边框 + 淡红底 */
.order-card.is-emergency {
  border-left-color: #f56c6c;
  background: rgba(245, 108, 108, 0.12);
}
/* 回退过的加急：橙色左边框 + 淡橙底 */
.order-card.is-urgent {
  border-left-color: #e6a23c;
  background: rgba(230, 162, 60, 0.12);
}
.oc-left {
  flex: 1 1 auto;
  min-width: 0;
}
.oc-line1 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 3px;
}
.oc-no {
  color: #6b8ca8;
  font-size: 12px;
}
.oc-name {
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.oc-line2 {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.oc-dept {
  color: #7f9bb3;
  margin-right: 10px;
}
.oc-fault {
  color: #5b7691;
}
.oc-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
}
.oc-wait {
  color: #7f9bb3;
  font-size: 13px;
  white-space: nowrap;
}
/* 超时工单：红色 + 闹钟图标 */
.oc-wait.is-overdue {
  color: #f56c6c;
  font-weight: 600;
}
.oc-clock {
  margin-right: 2px;
}
.oc-btn {
  color: #22d3ee !important;
  padding: 0;
}
.empty-tip {
  height: 100%;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7f9bb3;
  font-size: 14px;
}

/* ===== 监护明细（MONITOR DETAIL）===== */
.moni-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  line-height: 24px;
  border-bottom: 1px dashed rgba(27, 58, 92, 0.6);
}
.moni-row:last-child {
  border-bottom: none;
}
.moni-dept {
  color: #c8dbe8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.moni-num {
  color: #22d3ee;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.moni-num i {
  color: #6b8ca8;
  font-size: 10px;
  font-style: normal;
  font-weight: normal;
  margin-left: 2px;
}
/* 合计行：略高亮 */
/* 列头行：弱化灰字、不显示单位斜体，仅作表头说明 */
.moni-head {
  color: #6b8ca8;
  font-size: 11px;
  line-height: 22px;
  border-bottom: 1px solid #1b3a5c;
}
.moni-head .moni-dept,
.moni-head .moni-num {
  color: #6b8ca8;
  font-weight: normal;
  font-size: 11px;
}
.moni-sum {
  margin-top: 4px;
  border-top: 1px solid #1b3a5c;
  border-bottom: none;
  padding-top: 4px;
}
.moni-sum .moni-dept {
  color: #ffffff;
  font-weight: 600;
}
.moni-sum .moni-num {
  color: #ffffff;
}

/* ===== 到期提醒轮播：深色皮肤覆盖（组件自带样式是浅色渐变，这里改造成大屏风） ===== */
.carousel-bd {
  padding: 8px;
  overflow: hidden;
}
.carousel-bd /deep/ .expiry-carousel {
  margin: 0;
  width: 100%;
  min-width: 0;
}
.carousel-bd /deep/ .alert-card.overdue {
  background: linear-gradient(135deg, rgba(245, 108, 108, 0.2) 0%, rgba(245, 108, 108, 0.06) 100%);
  border-left-color: #f56c6c;
  color: #ffb3b3;
}
.carousel-bd /deep/ .alert-card.pending {
  background: linear-gradient(135deg, rgba(230, 162, 60, 0.2) 0%, rgba(230, 162, 60, 0.06) 100%);
  border-left-color: #e6a23c;
  color: #ffd79a;
}
.carousel-bd /deep/ .module-tag {
  background: rgba(255, 255, 255, 0.12);
  color: inherit;
}
.carousel-bd /deep/ .alert-card:hover {
  box-shadow: 0 0 10px rgba(34, 211, 238, 0.25);
}

/* ===== 细青色滚动条，贴合大屏风格 ===== */
.panel-bd::-webkit-scrollbar,
.col::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.panel-bd::-webkit-scrollbar-thumb,
.col::-webkit-scrollbar-thumb {
  background: rgba(34, 211, 238, 0.25);
  border-radius: 3px;
}
.panel-bd::-webkit-scrollbar-track,
.col::-webkit-scrollbar-track {
  background: transparent;
}
</style>
